import React, { useState, useMemo } from 'react';
import {
  Shield,
  ShieldAlert,
  ShieldCheck,
  Search,
  Filter,
  Download,
  Calendar,
  Clock,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  XCircle,
  Lock,
  Eye,
  FileText,
  Sliders,
  Users,
  Layers,
  TrendingUp,
  DollarSign,
  Database,
  Building2,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  X,
  Check,
  RefreshCw,
  ExternalLink,
  Key,
  Terminal,
  Hash,
  Bell,
  Cpu
} from 'lucide-react';
import { AppView } from '../../types';

interface AuditLogsManagementProps {
  onNavigateToView: (view: AppView) => void;
  onOpenSearch?: () => void;
}

export type SeverityLevel = 'CRITICAL' | 'WARNING' | 'INFO';
export type EnvironmentType = 'Production' | 'Staging' | 'Development';
export type AuditResult = 'Success' | 'Denied' | 'Approved' | 'Warning';

export interface AuditLogItem {
  id: string;
  timestamp: string; // UTC formatted
  rawEpoch: number;
  severity: SeverityLevel;
  actor: string;
  actorType: 'SERVICE_ACCOUNT' | 'USER' | 'AUTOMATED_JOB' | 'API' | 'CI_CD' | 'ADMIN_OVERRIDE';
  actorEmail?: string;
  action: string;
  resource: string;
  resourceCategory: 'MARKET_CONFIG' | 'FINANCIAL_RULESET' | 'FEATURE_FLAG' | 'DATA_ACCESS' | 'ROLE_IAM' | 'PROVIDER' | 'RELEASE' | 'AI_ROUTE';
  environment: EnvironmentType;
  result: AuditResult;
  ipAddress: string;
  geoLocation: string;
  userAgent: string;
  requestId: string;
  correlationId: string;
  merkleRootHash: string;
  details: string;
  beforeState?: Record<string, unknown>;
  afterState?: Record<string, unknown>;
}

export const AuditLogsManagement: React.FC<AuditLogsManagementProps> = ({
  onNavigateToView,
  onOpenSearch
}) => {
  const [selectedDomain, setSelectedDomain] = useState<string>('operations');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedResourceCategory, setSelectedResourceCategory] = useState<string>('ALL');
  const [selectedDateRange, setSelectedDateRange] = useState<string>('24h');
  const [selectedSeverity, setSelectedSeverity] = useState<string>('ALL');
  const [selectedEnvironment, setSelectedEnvironment] = useState<string>('ALL');
  const [selectedResult, setSelectedResult] = useState<string>('ALL');
  const [selectedActorType, setSelectedActorType] = useState<string>('ALL');

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 8;

  // Modals & Drawers
  const [selectedLog, setSelectedLog] = useState<AuditLogItem | null>(null);
  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const [showFilterModal, setShowFilterModal] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const triggerToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3500);
  };

  // Canonical YamooPay Audit Logs dataset (Faithful reproduction of screenshot + realistic audit entries)
  const auditLogsData: AuditLogItem[] = [
    {
      id: 'aud-001',
      timestamp: '2023-10-27 14:32:01',
      rawEpoch: 1698417121000,
      severity: 'CRITICAL',
      actor: 'system_service_account',
      actorType: 'SERVICE_ACCOUNT',
      action: 'UpdateMarketConfiguration',
      resource: 'cfg-market-eu-west-1',
      resourceCategory: 'MARKET_CONFIG',
      environment: 'Production',
      result: 'Success',
      ipAddress: '10.240.1.18 (VPC Internal)',
      geoLocation: 'Frankfurt (eu-central-1)',
      userAgent: 'Yamoo-Core-Engine/v3.4.1 (Kernel Orchestrator)',
      requestId: 'req_9af3bc81e280',
      correlationId: 'corr_yamoo_77a9c8',
      merkleRootHash: 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
      details: 'Automated adjustment of EUR settlement window buffers based on ECB target closing times.',
      beforeState: {
        market_code: 'EU-WEST-1',
        settlement_cutoff_utc: '16:00:00',
        instant_sepa_buffer_bps: 12,
        active_clearing_nodes: ['fra-01', 'fra-02']
      },
      afterState: {
        market_code: 'EU-WEST-1',
        settlement_cutoff_utc: '16:30:00',
        instant_sepa_buffer_bps: 10,
        active_clearing_nodes: ['fra-01', 'fra-02', 'fra-03']
      }
    },
    {
      id: 'aud-002',
      timestamp: '2023-10-27 14:28:45',
      rawEpoch: 1698416925000,
      severity: 'INFO',
      actor: 'j.doe@yamoopay.com',
      actorType: 'USER',
      actorEmail: 'j.doe@yamoopay.com',
      action: 'PublishFinancialRuleset',
      resource: 'ruleset-compliance-q4',
      resourceCategory: 'FINANCIAL_RULESET',
      environment: 'Staging',
      result: 'Success',
      ipAddress: '82.65.12.98 (Corporate VPN)',
      geoLocation: 'Paris, France',
      userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) YamooAdmin/4.2.0',
      requestId: 'req_77d29a141b9c',
      correlationId: 'corr_ruleset_9921',
      merkleRootHash: 'f412c980ae182103859841804918230491823094812304981203948120394812',
      details: 'Published updated statutory tax withholding matrix for French SAS and micro-enterprise payroll runs.',
      beforeState: {
        ruleset_id: 'ruleset-compliance-q4',
        status: 'DRAFT',
        version: 'v4.1.0-rc2',
        validated_by_cfo: true
      },
      afterState: {
        ruleset_id: 'ruleset-compliance-q4',
        status: 'PUBLISHED_STAGING',
        version: 'v4.1.0',
        published_at_utc: '2023-10-27T14:28:45Z'
      }
    },
    {
      id: 'aud-003',
      timestamp: '2023-10-27 14:15:12',
      rawEpoch: 1698416112000,
      severity: 'WARNING',
      actor: 'automated_sync_job',
      actorType: 'AUTOMATED_JOB',
      action: 'ChangeFeatureFlag',
      resource: 'ff-new-payment-flow',
      resourceCategory: 'FEATURE_FLAG',
      environment: 'Production',
      result: 'Success',
      ipAddress: '10.240.4.92 (Cron Worker)',
      geoLocation: 'Dublin (eu-west-1)',
      userAgent: 'Yamoo-CronDaemon/2.1.0',
      requestId: 'req_3289012480ad',
      correlationId: 'corr_ff_rollout_55',
      merkleRootHash: '9841209384102938412093841209384019283401928340192834019283401928',
      details: 'Canary rollout percentage ramped from 10% to 25% for frictionless one-click checkout across UK merchants.',
      beforeState: {
        flag_key: 'ff-new-payment-flow',
        rollout_percentage: 10,
        kill_switch_enabled: false
      },
      afterState: {
        flag_key: 'ff-new-payment-flow',
        rollout_percentage: 25,
        kill_switch_enabled: false
      }
    },
    {
      id: 'aud-004',
      timestamp: '2023-10-27 13:55:09',
      rawEpoch: 1698414909000,
      severity: 'INFO',
      actor: 'a.smith@yamoopay.com',
      actorType: 'USER',
      actorEmail: 'a.smith@yamoopay.com',
      action: 'ApproveDataAccess',
      resource: 'req-data-export-112',
      resourceCategory: 'DATA_ACCESS',
      environment: 'Production',
      result: 'Approved',
      ipAddress: '194.254.60.10 (Office Bastion)',
      geoLocation: 'London, UK',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) Chrome/118.0.0.0',
      requestId: 'req_120938401928',
      correlationId: 'corr_dsar_export_112',
      merkleRootHash: 'a129038401928340192834019283401928340192834019283401928340192834',
      details: 'Approved GDPR Article 15 Data Subject Access Request export package after customer identity notarization verification.',
      beforeState: {
        request_id: 'req-data-export-112',
        approval_status: 'PENDING_DPO_SIGN',
        customer_id_verified: true
      },
      afterState: {
        request_id: 'req-data-export-112',
        approval_status: 'APPROVED',
        encryption_key_id: '[REDACTED_AES256_KMS_KEY_REF]',
        expiry_hours: 48
      }
    },
    {
      id: 'aud-005',
      timestamp: '2023-10-27 13:42:22',
      rawEpoch: 1698414142000,
      severity: 'INFO',
      actor: '192.168.1.45 (API)',
      actorType: 'API',
      action: 'UpdateRole',
      resource: 'role-support-tier1',
      resourceCategory: 'ROLE_IAM',
      environment: 'Development',
      result: 'Success',
      ipAddress: '192.168.1.45 (Internal Dev Gateway)',
      geoLocation: 'Local Dev Subnet',
      userAgent: 'Yamoo-IAM-API/v1.0.0',
      requestId: 'req_884910283401',
      correlationId: 'corr_iam_role_mod_14',
      merkleRootHash: 'b948120394812039481203948120394812039481203948120394812039481203',
      details: 'Added read-only permission scope `dispute.evidence.read` to Tier 1 Customer Support role.',
      beforeState: {
        role_name: 'role-support-tier1',
        permissions: ['tickets.read', 'tickets.respond', 'orders.read']
      },
      afterState: {
        role_name: 'role-support-tier1',
        permissions: ['tickets.read', 'tickets.respond', 'orders.read', 'dispute.evidence.read']
      }
    },
    {
      id: 'aud-006',
      timestamp: '2023-10-27 13:10:05',
      rawEpoch: 1698412205000,
      severity: 'INFO',
      actor: 'm.jones@yamoopay.com',
      actorType: 'USER',
      actorEmail: 'm.jones@yamoopay.com',
      action: 'ActivateProvider',
      resource: 'prv-stripe-main',
      resourceCategory: 'PROVIDER',
      environment: 'Production',
      result: 'Success',
      ipAddress: '90.112.44.18 (Encrypted Tunnel)',
      geoLocation: 'Amsterdam, Netherlands',
      userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)',
      requestId: 'req_448102938401',
      correlationId: 'corr_gateway_prov_02',
      merkleRootHash: 'c129038401928340192834019283401928340192834019283401928340192834',
      details: 'Activated secondary multi-acquirer fallback route for Visa 3DS v2.2 cardholder verification.',
      beforeState: {
        provider_id: 'prv-stripe-main',
        status: 'STANDBY',
        traffic_weight: 0
      },
      afterState: {
        provider_id: 'prv-stripe-main',
        status: 'ACTIVE_PRIMARY',
        traffic_weight: 80,
        fallback_weight: 20
      }
    },
    {
      id: 'aud-007',
      timestamp: '2023-10-27 12:45:33',
      rawEpoch: 1698410733000,
      severity: 'INFO',
      actor: 'ci_cd_pipeline',
      actorType: 'CI_CD',
      action: 'DeployRelease',
      resource: 'rel-v2.4.1',
      resourceCategory: 'RELEASE',
      environment: 'Staging',
      result: 'Success',
      ipAddress: '34.240.19.112 (GitHub Actions Runner)',
      geoLocation: 'Dublin, Ireland',
      userAgent: 'GitHub-Actions/Runner-2.311.0',
      requestId: 'req_991823094812',
      correlationId: 'corr_release_deploy_241',
      merkleRootHash: 'd948120394812039481203948120394812039481203948120394812039481203',
      details: 'Deployed microservice image `yamoo-settlement-engine:v2.4.1` with statutory round-trip latency optimizations.',
      beforeState: {
        release_tag: 'v2.4.0',
        commit_sha: '7a9c8f1',
        nodes_healthy: 6
      },
      afterState: {
        release_tag: 'v2.4.1',
        commit_sha: 'e4b109d',
        nodes_healthy: 6
      }
    },
    {
      id: 'aud-008',
      timestamp: '2023-10-27 12:15:00',
      rawEpoch: 1698408900000,
      severity: 'CRITICAL',
      actor: 'admin_override',
      actorType: 'ADMIN_OVERRIDE',
      action: 'ChangeAIRoute',
      resource: 'route-risk-model-v3',
      resourceCategory: 'AI_ROUTE',
      environment: 'Production',
      result: 'Success',
      ipAddress: '194.254.60.10 (CISO Emergency Terminal)',
      geoLocation: 'London, UK',
      userAgent: 'Yamoo-BreakGlass-CLI/1.0.0 (Dual-Signed)',
      requestId: 'req_emergency_ai_99',
      correlationId: 'corr_ciso_override_01',
      merkleRootHash: 'e129038401928340192834019283401928340192834019283401928340192834',
      details: 'Emergency manual failover of real-time transaction scoring model from external cloud endpoint to sovereign on-premise inference cluster.',
      beforeState: {
        route_id: 'route-risk-model-v3',
        primary_endpoint: 'https://api.external-ai.provider/v1/score',
        routing_mode: 'COST_OPTIMIZED'
      },
      afterState: {
        route_id: 'route-risk-model-v3',
        primary_endpoint: 'https://sovereign-cluster.internal.yamoo/v1/score',
        routing_mode: 'SOVEREIGN_AIR_GAPPED',
        break_glass_reason: 'External provider latency spike detected > 1200ms'
      }
    },
    // Additional realistic records for pagination and full test coverage
    {
      id: 'aud-009',
      timestamp: '2023-10-27 11:50:18',
      rawEpoch: 1698407418000,
      severity: 'WARNING',
      actor: 'security_scanner',
      actorType: 'AUTOMATED_JOB',
      action: 'ApproveDataAccess',
      resource: 'req-token-refresh-88',
      resourceCategory: 'DATA_ACCESS',
      environment: 'Production',
      result: 'Denied',
      ipAddress: '10.240.9.11',
      geoLocation: 'Frankfurt, Germany',
      userAgent: 'Yamoo-Guard-Scanner/v2.1',
      requestId: 'req_scan_deny_44',
      correlationId: 'corr_guard_09',
      merkleRootHash: 'f192038401928340192834019283401928340192834019283401928340192834',
      details: 'Blocked unauthorized automated credential rotation due to unverified IP origin.',
      beforeState: { token_id: 'tok_***', status: 'ACTIVE' },
      afterState: { token_id: 'tok_***', status: 'LOCKED', alert_sent: true }
    },
    {
      id: 'aud-010',
      timestamp: '2023-10-27 11:22:04',
      rawEpoch: 1698405724000,
      severity: 'INFO',
      actor: 'sarah.c@yamoopay.com',
      actorType: 'USER',
      actorEmail: 'sarah.c@yamoopay.com',
      action: 'UpdateMarketConfiguration',
      resource: 'cfg-market-us-east-1',
      resourceCategory: 'MARKET_CONFIG',
      environment: 'Production',
      result: 'Success',
      ipAddress: '84.110.22.90',
      geoLocation: 'New York, US',
      userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)',
      requestId: 'req_us_market_01',
      correlationId: 'corr_market_us_99',
      merkleRootHash: '1234c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
      details: 'Enabled ACH Same-Day batch submission window for US domestic business payroll.',
      beforeState: { market: 'US-EAST-1', same_day_ach: false },
      afterState: { market: 'US-EAST-1', same_day_ach: true, cutoff_est: '14:45' }
    }
  ];

  // Filtering Logic
  const filteredLogs = useMemo(() => {
    return auditLogsData.filter((log) => {
      // 1. Search Query
      if (searchQuery.trim() !== '') {
        const q = searchQuery.toLowerCase();
        const match =
          log.actor.toLowerCase().includes(q) ||
          log.action.toLowerCase().includes(q) ||
          log.resource.toLowerCase().includes(q) ||
          log.requestId.toLowerCase().includes(q) ||
          log.correlationId.toLowerCase().includes(q) ||
          log.environment.toLowerCase().includes(q) ||
          log.details.toLowerCase().includes(q);
        if (!match) return false;
      }

      // 2. Resource Category Filter
      if (selectedResourceCategory !== 'ALL' && log.resourceCategory !== selectedResourceCategory) {
        return false;
      }

      // 3. Severity Filter
      if (selectedSeverity !== 'ALL' && log.severity !== selectedSeverity) {
        return false;
      }

      // 4. Environment Filter
      if (selectedEnvironment !== 'ALL' && log.environment !== selectedEnvironment) {
        return false;
      }

      // 5. Result Filter
      if (selectedResult !== 'ALL' && log.result !== selectedResult) {
        return false;
      }

      // 6. Actor Type Filter
      if (selectedActorType !== 'ALL' && log.actorType !== selectedActorType) {
        return false;
      }

      return true;
    });
  }, [
    auditLogsData,
    searchQuery,
    selectedResourceCategory,
    selectedSeverity,
    selectedEnvironment,
    selectedResult,
    selectedActorType
  ]);

  // Paginated Slices
  const totalEntries = 1249; // Matching screenshot total count display: "Showing 1 to 8 of 1,249 entries (Demo)"
  const displayedLogs = filteredLogs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Helper for Severity Circle Indicator
  const renderSeverityDot = (severity: SeverityLevel) => {
    switch (severity) {
      case 'CRITICAL':
        return (
          <div className="w-2.5 h-2.5 rounded-full bg-[#EF4444] shadow-xs" title="Critical / High Severity" />
        );
      case 'WARNING':
        return (
          <div className="w-2.5 h-2.5 rounded-full bg-[#F59E0B] shadow-xs" title="Warning / Moderate Severity" />
        );
      case 'INFO':
      default:
        return (
          <div className="w-2.5 h-2.5 rounded-full bg-[#22C55E] shadow-xs" title="Normal / Info" />
        );
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex text-[#1E293B] font-sans antialiased">
      {/* =============================================================== */}
      {/* 1. LEFT SIDEBAR (Canonical 7 Domains, with Operations active)   */}
      {/* =============================================================== */}
      <aside className="w-64 bg-white border-r border-[#E2E8F0] flex flex-col shrink-0 min-h-screen sticky top-0 h-screen overflow-y-auto">
        {/* Brand Header */}
        <div className="p-5 border-b border-[#F1F5F9] flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#0F4F4F] flex items-center justify-center text-white shadow-xs font-black text-lg">
            <span className="font-black text-white text-base leading-none">Y</span>
          </div>
          <div>
            <h1 className="text-base font-extrabold text-[#0F172A] tracking-tight leading-none">
              Control Center
            </h1>
            <span className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider block mt-1">
              Administrator
            </span>
          </div>
        </div>

        {/* Sidebar Navigation Links */}
        <div className="flex-1 py-4 px-3 space-y-1">
          <button
            onClick={() => {
              setSelectedDomain('overview');
              onNavigateToView('admin-super-command-center');
            }}
            className="w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-all cursor-pointer"
          >
            <Layers className="w-4 h-4" />
            <span>Overview</span>
          </button>

          <button
            onClick={() => {
              setSelectedDomain('customers');
              onNavigateToView('admin-organizations');
            }}
            className="w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-all cursor-pointer"
          >
            <Users className="w-4 h-4" />
            <span>Customers & Support</span>
          </button>

          <button
            onClick={() => {
              setSelectedDomain('product');
              onNavigateToView('cpo-command-center');
            }}
            className="w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-all cursor-pointer"
          >
            <TrendingUp className="w-4 h-4" />
            <span>Product & Growth</span>
          </button>

          <button
            onClick={() => {
              setSelectedDomain('revenue');
              onNavigateToView('admin-monetization');
            }}
            className="w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-all cursor-pointer"
          >
            <DollarSign className="w-4 h-4" />
            <span>Revenue & Finance</span>
          </button>

          <button
            onClick={() => {
              setSelectedDomain('data');
              onNavigateToView('admin-ai-platform');
            }}
            className="w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-all cursor-pointer"
          >
            <Database className="w-4 h-4" />
            <span>Data & AI</span>
          </button>

          {/* ACTIVE: Operations & Risk (Minty/emerald background matching screenshot) */}
          <div>
            <button
              onClick={() => setSelectedDomain('operations')}
              className="w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-bold bg-[#E6F7F0] text-[#0E7044] transition-all cursor-pointer"
            >
              <ShieldCheck className="w-4 h-4 text-[#0E7044]" />
              <span>Operations & Risk</span>
            </button>

            {/* Sub-menu under Operations & Risk */}
            <div className="ml-4 pl-3 border-l-2 border-[#0E7044] my-1 space-y-1">
              <button
                onClick={() => onNavigateToView('admin-compliance')}
                className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium text-[#64748B] hover:text-[#0F172A] transition-colors cursor-pointer"
              >
                Compliance Monitor
              </button>
              <button
                className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-bold text-[#0E7044] bg-[#E6F7F0]/60 transition-colors cursor-pointer"
              >
                Audit Logs
              </button>
              <button
                onClick={() => onNavigateToView('admin-reports')}
                className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium text-[#64748B] hover:text-[#0F172A] transition-colors cursor-pointer"
              >
                Audit & Reports
              </button>
              <button
                onClick={() => onNavigateToView('admin-system-health')}
                className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium text-[#64748B] hover:text-[#0F172A] transition-colors cursor-pointer"
              >
                System Health
              </button>
              <button
                onClick={() => onNavigateToView('ciso-security-command-center')}
                className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium text-[#64748B] hover:text-[#0F172A] transition-colors cursor-pointer"
              >
                CISO Security
              </button>
            </div>
          </div>

          <button
            onClick={() => {
              setSelectedDomain('configuration');
              onNavigateToView('admin-configuration');
            }}
            className="w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-all cursor-pointer"
          >
            <Sliders className="w-4 h-4" />
            <span>Configuration</span>
          </button>
        </div>

        {/* Sidebar Footer Profile */}
        <div className="p-3 border-t border-[#F1F5F9]">
          <div className="flex items-center gap-3 p-2 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
            <div className="w-8 h-8 rounded-full bg-[#0F4F4F] text-white font-bold text-xs flex items-center justify-center">
              AD
            </div>
            <div className="min-w-0 flex-1">
              <span className="text-xs font-bold text-[#0F172A] block truncate">Security Auditor</span>
              <span className="text-[10px] font-mono text-[#64748B] block truncate">WORM Immutability Active</span>
            </div>
          </div>
        </div>
      </aside>

      {/* =============================================================== */}
      {/* 2. MAIN CONTENT AREA                                           */}
      {/* =============================================================== */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header Bar with Global Search (Exact match to screenshot) */}
        <header className="h-16 bg-white border-b border-[#E2E8F0] px-8 flex items-center justify-between sticky top-0 z-20 gap-6">
          <div className="flex-1 max-w-2xl relative">
            <div className="relative">
              <Search className="w-4 h-4 text-[#94A3B8] absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search resources, users, or action IDs (Demo)"
                className="w-full pl-10 pr-4 py-2 bg-white border border-[#E2E8F0] rounded-xl text-xs text-[#0F172A] placeholder-[#94A3B8] outline-none focus:border-[#0F4F4F] focus:ring-1 focus:ring-[#0F4F4F] transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4 shrink-0">
            <button
              onClick={() => triggerToast('WORM Storage: Cryptographic integrity seal active (SHA-256 Merkle chain).')}
              className="text-[#64748B] hover:text-[#0F172A] p-2 rounded-lg hover:bg-[#F1F5F9] transition-colors cursor-pointer"
              title="Tamper-proof Seal Active"
            >
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
            </button>
            <button
              onClick={() => triggerToast('Date Range: Active window UTC filtered.')}
              className="text-[#64748B] hover:text-[#0F172A] p-2 rounded-lg hover:bg-[#F1F5F9] transition-colors cursor-pointer"
              title="Time Range"
            >
              <Calendar className="w-4 h-4" />
            </button>
            <button
              onClick={() => triggerToast('Zero security alerting escalations in current session.')}
              className="text-[#64748B] hover:text-[#0F172A] p-2 rounded-lg hover:bg-[#F1F5F9] transition-colors cursor-pointer"
              title="Notifications"
            >
              <Bell className="w-4 h-4" />
            </button>
            <div className="w-8 h-8 rounded-full bg-[#E2E8F0] flex items-center justify-center text-[#475569] text-xs font-bold">
              AD
            </div>
          </div>
        </header>

        {/* PAGE BODY */}
        <main className="p-8 space-y-6 max-w-7xl mx-auto w-full">
          {/* Toast Notification */}
          {toastMessage && (
            <div className="fixed bottom-6 right-6 z-50 bg-[#0F172A] text-white text-xs font-bold px-4 py-3 rounded-xl shadow-2xl flex items-center gap-2 animate-in slide-in-from-bottom-5 duration-200">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <span>{toastMessage}</span>
            </div>
          )}

          {/* =============================================================== */}
          {/* TITLE, SUBTITLE & TOP RIGHT FILTERS / EXPORT                    */}
          {/* =============================================================== */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2.5">
                <h2 className="text-xl font-extrabold text-[#0F172A] tracking-tight">
                  Audit Logs
                </h2>
                <span className="px-2 py-0.5 bg-[#EFF6FF] text-[#2563EB] border border-[#BFDBFE] text-[10px] font-bold rounded-md">
                  Demo Data
                </span>
                <span className="px-2 py-0.5 bg-[#F0FDF4] text-[#16A34A] border border-[#BBF7D0] text-[10px] font-bold rounded-md flex items-center gap-1">
                  <Lock className="w-2.5 h-2.5" />
                  <span>Immutable (Read-Only)</span>
                </span>
              </div>
              <p className="text-xs text-[#64748B] mt-1">
                System-wide immutable record of configuration and access events. (Demo Data)
              </p>
            </div>

            {/* Filter Dropdowns & Export Button (Exact match to screenshot) */}
            <div className="flex items-center gap-3 flex-wrap">
              {/* Dropdown 1: All Resources */}
              <div className="relative">
                <select
                  value={selectedResourceCategory}
                  onChange={(e) => {
                    setSelectedResourceCategory(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="appearance-none bg-white border border-[#CBD5E1] text-[#334155] rounded-xl px-4 py-2 pr-9 text-xs font-semibold shadow-2xs outline-none cursor-pointer hover:bg-[#F8FAFC] transition-colors"
                >
                  <option value="ALL">All Resources</option>
                  <option value="MARKET_CONFIG">Market Config</option>
                  <option value="FINANCIAL_RULESET">Financial Rulesets</option>
                  <option value="FEATURE_FLAG">Feature Flags</option>
                  <option value="DATA_ACCESS">Data Access (GDPR/DSAR)</option>
                  <option value="ROLE_IAM">Roles & IAM</option>
                  <option value="PROVIDER">Payment Providers</option>
                  <option value="RELEASE">Release Deployments</option>
                  <option value="AI_ROUTE">AI Routes</option>
                </select>
                <ChevronDown className="w-3.5 h-3.5 text-[#64748B] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>

              {/* Dropdown 2: Last 24 Hours */}
              <div className="relative">
                <select
                  value={selectedDateRange}
                  onChange={(e) => {
                    setSelectedDateRange(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="appearance-none bg-white border border-[#CBD5E1] text-[#334155] rounded-xl px-4 py-2 pr-9 text-xs font-semibold shadow-2xs outline-none cursor-pointer hover:bg-[#F8FAFC] transition-colors"
                >
                  <option value="15m">Last 15 Minutes</option>
                  <option value="1h">Last 1 Hour</option>
                  <option value="24h">Last 24 Hours</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                </select>
                <ChevronDown className="w-3.5 h-3.5 text-[#64748B] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>

              {/* Advanced Filter Modal Trigger */}
              <button
                onClick={() => setShowFilterModal(true)}
                className="px-3.5 py-2 bg-white hover:bg-[#F8FAFC] text-[#475569] border border-[#CBD5E1] rounded-xl text-xs font-semibold flex items-center gap-1.5 shadow-2xs transition-colors cursor-pointer"
                title="More Filters"
              >
                <Filter className="w-3.5 h-3.5 text-[#64748B]" />
                <span>Filters</span>
                {(selectedSeverity !== 'ALL' || selectedEnvironment !== 'ALL' || selectedResult !== 'ALL') && (
                  <span className="w-2 h-2 rounded-full bg-[#0E7044]"></span>
                )}
              </button>

              {/* Export CSV Button (Dark Forest Teal `#0F4F4F` matching screenshot) */}
              <button
                onClick={() => setShowExportModal(true)}
                className="px-4 py-2 bg-[#0F4F4F] hover:bg-[#0A3D3D] text-white rounded-xl text-xs font-bold flex items-center gap-2 shadow-xs transition-colors cursor-pointer"
              >
                <Download className="w-3.5 h-3.5 text-white" />
                <span>Export CSV</span>
              </button>
            </div>
          </div>

          {/* =============================================================== */}
          {/* AUDIT LOGS TABLE CONTAINER (Exact match to screenshot)          */}
          {/* =============================================================== */}
          <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-2xs overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="border-b border-[#F1F5F9] text-[#64748B] font-semibold text-[11px] bg-[#F8FAFC]/60">
                    <th className="py-3.5 px-6 font-semibold">Timestamp (UTC)</th>
                    <th className="py-3.5 px-3 font-semibold text-center w-12">Sev</th>
                    <th className="py-3.5 px-4 font-semibold">Actor</th>
                    <th className="py-3.5 px-4 font-semibold">Action</th>
                    <th className="py-3.5 px-4 font-semibold">Resource</th>
                    <th className="py-3.5 px-4 font-semibold">Environment</th>
                    <th className="py-3.5 px-6 font-semibold text-right">Result</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {displayedLogs.map((log) => (
                    <tr
                      key={log.id}
                      onClick={() => setSelectedLog(log)}
                      className="hover:bg-[#F8FAFC] transition-colors cursor-pointer group"
                    >
                      {/* Timestamp (UTC) */}
                      <td className="py-4 px-6 font-mono text-[11px] text-[#334155] whitespace-nowrap">
                        {log.timestamp}
                      </td>

                      {/* Severity Circle Dot */}
                      <td className="py-4 px-3 text-center whitespace-nowrap">
                        <div className="flex items-center justify-center">
                          {renderSeverityDot(log.severity)}
                        </div>
                      </td>

                      {/* Actor */}
                      <td className="py-4 px-4 text-[#0F172A] font-medium whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <span>{log.actor}</span>
                        </div>
                      </td>

                      {/* Action */}
                      <td className="py-4 px-4 font-bold text-[#0F172A] whitespace-nowrap">
                        {log.action}
                      </td>

                      {/* Resource (Clickable Blue Link style from screenshot) */}
                      <td className="py-4 px-4 whitespace-nowrap">
                        <span className="text-[#2563EB] hover:underline font-medium">
                          {log.resource}
                        </span>
                      </td>

                      {/* Environment */}
                      <td className="py-4 px-4 text-[#475569] whitespace-nowrap">
                        {log.environment}
                      </td>

                      {/* Result */}
                      <td className="py-4 px-6 text-right whitespace-nowrap">
                        {log.result === 'Success' && (
                          <span className="inline-flex items-center gap-1 text-[#16A34A] font-semibold text-xs">
                            <Check className="w-3.5 h-3.5" />
                            <span>Success</span>
                          </span>
                        )}
                        {log.result === 'Approved' && (
                          <span className="inline-flex items-center gap-1 text-[#2563EB] font-semibold text-xs">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            <span>Approved</span>
                          </span>
                        )}
                        {log.result === 'Denied' && (
                          <span className="inline-flex items-center gap-1 text-[#DC2626] font-semibold text-xs">
                            <XCircle className="w-3.5 h-3.5" />
                            <span>Denied</span>
                          </span>
                        )}
                        {log.result === 'Warning' && (
                          <span className="inline-flex items-center gap-1 text-[#D97706] font-semibold text-xs">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            <span>Warning</span>
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}

                  {displayedLogs.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-[#64748B]">
                        <Search className="w-8 h-8 text-[#CBD5E1] mx-auto mb-2" />
                        <span className="font-bold text-sm text-[#0F172A] block">No audit records found</span>
                        <span className="text-xs text-[#94A3B8] block mt-0.5">
                          Try adjusting your active search keywords or filter constraints.
                        </span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* =============================================================== */}
            {/* TABLE FOOTER & PAGINATION (Exact match to screenshot)           */}
            {/* =============================================================== */}
            <div className="p-4 border-t border-[#F1F5F9] bg-[#FAFAFA] flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-xs text-[#64748B]">
              <div>
                Showing <span className="font-semibold text-[#0F172A]">1</span> to{' '}
                <span className="font-semibold text-[#0F172A]">{displayedLogs.length}</span> of{' '}
                <span className="font-semibold text-[#0F172A]">{totalEntries.toLocaleString()}</span> entries (Demo)
              </div>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 bg-white border border-[#E2E8F0] rounded-lg text-xs font-semibold text-[#334155] hover:bg-[#F1F5F9] disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                >
                  Prev
                </button>

                <button
                  onClick={() => setCurrentPage(1)}
                  className={`w-8 h-8 rounded-lg text-xs font-bold transition-colors cursor-pointer ${
                    currentPage === 1
                      ? 'bg-white border border-[#CBD5E1] text-[#0F172A] shadow-2xs'
                      : 'text-[#64748B] hover:bg-[#F1F5F9]'
                  }`}
                >
                  1
                </button>

                <button
                  onClick={() => setCurrentPage(2)}
                  className={`w-8 h-8 rounded-lg text-xs font-bold transition-colors cursor-pointer ${
                    currentPage === 2
                      ? 'bg-white border border-[#CBD5E1] text-[#0F172A] shadow-2xs'
                      : 'text-[#64748B] hover:bg-[#F1F5F9]'
                  }`}
                >
                  2
                </button>

                <button
                  onClick={() => setCurrentPage(3)}
                  className={`w-8 h-8 rounded-lg text-xs font-bold transition-colors cursor-pointer ${
                    currentPage === 3
                      ? 'bg-white border border-[#CBD5E1] text-[#0F172A] shadow-2xs'
                      : 'text-[#64748B] hover:bg-[#F1F5F9]'
                  }`}
                >
                  3
                </button>

                <span className="px-1 text-[#94A3B8]">...</span>

                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  className="px-3 py-1.5 bg-white border border-[#E2E8F0] rounded-lg text-xs font-semibold text-[#334155] hover:bg-[#F1F5F9] cursor-pointer transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* =============================================================== */}
      {/* 3. AUDIT LOG DETAIL DRAWER (Read-Only & Immutability Guaranteed)  */}
      {/* =============================================================== */}
      {selectedLog && (
        <div className="fixed inset-0 bg-[#0F172A]/50 backdrop-blur-xs z-50 flex items-center justify-end">
          <div className="bg-white w-full max-w-2xl h-full shadow-2xl border-l border-[#E2E8F0] p-6 space-y-6 overflow-y-auto flex flex-col justify-between animate-in slide-in-from-right duration-300">
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-start justify-between pb-4 border-b border-[#F1F5F9]">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 bg-gray-100 text-[#0F172A] rounded text-[11px] font-mono font-bold">
                      {selectedLog.id}
                    </span>
                    <span className="px-2 py-0.5 bg-[#EFF6FF] text-[#2563EB] rounded text-[11px] font-semibold">
                      {selectedLog.environment}
                    </span>
                    <span className="text-xs font-mono text-[#64748B]">{selectedLog.timestamp} UTC</span>
                  </div>
                  <h3 className="text-lg font-bold text-[#0F172A] mt-2">
                    {selectedLog.action}
                  </h3>
                  <p className="text-xs text-[#64748B] mt-0.5">Target: <span className="font-semibold text-[#2563EB]">{selectedLog.resource}</span></p>
                </div>
                <button
                  onClick={() => setSelectedLog(null)}
                  className="p-1 text-[#94A3B8] hover:text-[#0F172A] rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Immutability Seal Banner */}
              <div className="p-3.5 bg-[#F0FDF4] border border-[#BBF7D0] rounded-xl flex items-start gap-2.5 text-xs text-[#166534]">
                <ShieldCheck className="w-4 h-4 text-[#16A34A] shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold block">Cryptographically Sealed WORM Record</span>
                  <span className="text-[11px] text-[#15803D] mt-0.5 block">
                    This entry is permanently signed and immutable. Editing or deletion is strictly forbidden by policy and cryptographic ledger hash.
                  </span>
                </div>
              </div>

              {/* Actor & Execution Metadata Grid */}
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="bg-[#F8FAFC] p-3 rounded-xl border border-[#E2E8F0]">
                  <span className="text-[10px] font-bold text-[#64748B] uppercase block">Actor</span>
                  <span className="font-bold text-[#0F172A] text-xs mt-1 block">{selectedLog.actor}</span>
                  <span className="text-[10px] text-[#64748B] font-mono">{selectedLog.actorType}</span>
                </div>

                <div className="bg-[#F8FAFC] p-3 rounded-xl border border-[#E2E8F0]">
                  <span className="text-[10px] font-bold text-[#64748B] uppercase block">IP Address & Origin</span>
                  <span className="font-mono font-bold text-[#0F172A] text-xs mt-1 block">{selectedLog.ipAddress}</span>
                  <span className="text-[10px] text-[#64748B]">{selectedLog.geoLocation}</span>
                </div>

                <div className="bg-[#F8FAFC] p-3 rounded-xl border border-[#E2E8F0]">
                  <span className="text-[10px] font-bold text-[#64748B] uppercase block">Request ID</span>
                  <span className="font-mono text-[#0F172A] text-xs mt-1 block truncate" title={selectedLog.requestId}>
                    {selectedLog.requestId}
                  </span>
                </div>

                <div className="bg-[#F8FAFC] p-3 rounded-xl border border-[#E2E8F0]">
                  <span className="text-[10px] font-bold text-[#64748B] uppercase block">Correlation ID</span>
                  <span className="font-mono text-[#0F172A] text-xs mt-1 block truncate" title={selectedLog.correlationId}>
                    {selectedLog.correlationId}
                  </span>
                </div>
              </div>

              {/* Event Description */}
              <div className="space-y-1.5 text-xs">
                <label className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider block">
                  Event Summary & Justification
                </label>
                <p className="text-[#334155] leading-relaxed bg-[#F8FAFC] p-3 rounded-xl border border-[#E2E8F0]">
                  {selectedLog.details}
                </p>
              </div>

              {/* Merkle Hash Proof */}
              <div className="space-y-1.5 text-xs">
                <label className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider flex items-center gap-1.5">
                  <Hash className="w-3 h-3 text-[#64748B]" />
                  <span>SHA-256 Merkle Ledger Proof</span>
                </label>
                <div className="font-mono text-[10px] text-[#475569] bg-[#F1F5F9] p-2.5 rounded-xl border border-[#E2E8F0] break-all select-all">
                  {selectedLog.merkleRootHash}
                </div>
              </div>

              {/* Before / After State (Strictly Redacted for Secrets) */}
              <div className="space-y-3 text-xs">
                <label className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider block">
                  State Mutation Diff (Sanitized)
                </label>

                <div className="grid grid-cols-2 gap-3 font-mono text-[11px]">
                  <div className="bg-red-50/60 border border-red-100 rounded-xl p-3">
                    <span className="text-[10px] font-bold text-red-700 block mb-1.5">Previous State</span>
                    <pre className="text-red-900 overflow-x-auto whitespace-pre-wrap">
                      {JSON.stringify(selectedLog.beforeState || { status: 'INITIAL' }, null, 2)}
                    </pre>
                  </div>

                  <div className="bg-emerald-50/60 border border-emerald-100 rounded-xl p-3">
                    <span className="text-[10px] font-bold text-emerald-700 block mb-1.5">New State</span>
                    <pre className="text-emerald-900 overflow-x-auto whitespace-pre-wrap">
                      {JSON.stringify(selectedLog.afterState || { status: 'COMMITTED' }, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-[#F1F5F9] text-xs">
              <span className="text-[11px] text-[#94A3B8] flex items-center gap-1">
                <Lock className="w-3 h-3" />
                <span>Read-Only Audit Trail</span>
              </span>

              <div className="flex gap-2">
                <button
                  onClick={() => {
                    navigator.clipboard?.writeText(JSON.stringify(selectedLog, null, 2));
                    triggerToast(`Copied JSON payload for ${selectedLog.id}`);
                  }}
                  className="px-3 py-1.5 border border-[#CBD5E1] hover:bg-[#F8FAFC] text-[#475569] font-bold rounded-xl"
                >
                  Copy JSON
                </button>
                <button
                  onClick={() => setSelectedLog(null)}
                  className="px-4 py-1.5 bg-[#0F172A] hover:bg-[#1E293B] text-white font-bold rounded-xl"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =============================================================== */}
      {/* 4. ADVANCED MULTI-FILTER MODAL                                  */}
      {/* =============================================================== */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-[#0F172A]/50 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-[#E2E8F0] space-y-5">
            <div className="flex items-center justify-between pb-3 border-b border-[#F1F5F9]">
              <div>
                <h3 className="text-sm font-bold text-[#0F172A]">Filter Audit Trail</h3>
                <p className="text-[11px] text-[#64748B]">Apply multi-dimensional audit query filters</p>
              </div>
              <button
                onClick={() => setShowFilterModal(false)}
                className="p-1 text-[#94A3B8] hover:text-[#0F172A] rounded-lg"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3.5 text-xs">
              <div>
                <label className="text-[11px] font-bold text-[#64748B] block mb-1">Severity Level</label>
                <select
                  value={selectedSeverity}
                  onChange={(e) => setSelectedSeverity(e.target.value)}
                  className="w-full p-2.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl outline-none"
                >
                  <option value="ALL">All Severities</option>
                  <option value="CRITICAL">Critical (Red)</option>
                  <option value="WARNING">Warning (Amber)</option>
                  <option value="INFO">Info (Green)</option>
                </select>
              </div>

              <div>
                <label className="text-[11px] font-bold text-[#64748B] block mb-1">Environment</label>
                <select
                  value={selectedEnvironment}
                  onChange={(e) => setSelectedEnvironment(e.target.value)}
                  className="w-full p-2.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl outline-none"
                >
                  <option value="ALL">All Environments</option>
                  <option value="Production">Production</option>
                  <option value="Staging">Staging</option>
                  <option value="Development">Development</option>
                </select>
              </div>

              <div>
                <label className="text-[11px] font-bold text-[#64748B] block mb-1">Execution Result</label>
                <select
                  value={selectedResult}
                  onChange={(e) => setSelectedResult(e.target.value)}
                  className="w-full p-2.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl outline-none"
                >
                  <option value="ALL">All Results</option>
                  <option value="Success">Success</option>
                  <option value="Approved">Approved</option>
                  <option value="Denied">Denied</option>
                  <option value="Warning">Warning</option>
                </select>
              </div>

              <div>
                <label className="text-[11px] font-bold text-[#64748B] block mb-1">Actor Category</label>
                <select
                  value={selectedActorType}
                  onChange={(e) => setSelectedActorType(e.target.value)}
                  className="w-full p-2.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl outline-none"
                >
                  <option value="ALL">All Actors</option>
                  <option value="USER">Human Administrator</option>
                  <option value="SERVICE_ACCOUNT">Service Account</option>
                  <option value="AUTOMATED_JOB">Automated Cron Job</option>
                  <option value="CI_CD">CI/CD Pipeline</option>
                  <option value="ADMIN_OVERRIDE">Admin Break-Glass Override</option>
                </select>
              </div>
            </div>

            <div className="flex justify-between items-center pt-3 border-t border-[#F1F5F9]">
              <button
                onClick={() => {
                  setSelectedSeverity('ALL');
                  setSelectedEnvironment('ALL');
                  setSelectedResult('ALL');
                  setSelectedActorType('ALL');
                  setSelectedResourceCategory('ALL');
                }}
                className="text-[11px] text-[#2563EB] font-semibold hover:underline"
              >
                Reset All
              </button>

              <div className="flex gap-2">
                <button
                  onClick={() => setShowFilterModal(false)}
                  className="px-4 py-2 border border-[#CBD5E1] text-[#475569] font-bold rounded-xl text-xs"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setCurrentPage(1);
                    setShowFilterModal(false);
                  }}
                  className="px-4 py-2 bg-[#0F4F4F] hover:bg-[#0A3D3D] text-white font-bold rounded-xl text-xs"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =============================================================== */}
      {/* 5. EXPORT CSV MODAL                                             */}
      {/* =============================================================== */}
      {showExportModal && (
        <div className="fixed inset-0 bg-[#0F172A]/50 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-[#E2E8F0] space-y-5">
            <div className="flex items-center justify-between pb-3 border-b border-[#F1F5F9]">
              <div>
                <h3 className="text-sm font-bold text-[#0F172A]">Export Audit Trail</h3>
                <p className="text-[11px] text-[#64748B]">Download immutable audit logs with SHA-256 seal</p>
              </div>
              <button
                onClick={() => setShowExportModal(false)}
                className="p-1 text-[#94A3B8] hover:text-[#0F172A] rounded-lg"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div>
                <label className="text-[11px] font-bold text-[#64748B] block mb-1">Export Format</label>
                <select className="w-full p-2.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl outline-none">
                  <option value="CSV">Comma-Separated Values (.csv)</option>
                  <option value="JSON">JSON Ledger Archive (.jsonl)</option>
                  <option value="SIEM">CEF / Splunk Syslog Format</option>
                </select>
              </div>

              <div>
                <label className="text-[11px] font-bold text-[#64748B] block mb-1">Scope</label>
                <select className="w-full p-2.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl outline-none">
                  <option value="FILTERED">Current Filtered View ({filteredLogs.length} records)</option>
                  <option value="ALL">Full 24-Hour Archive ({totalEntries} records)</option>
                </select>
              </div>

              <div className="p-3 bg-blue-50/60 border border-blue-100 rounded-xl text-[11px] text-[#1E40AF]">
                <span className="font-bold block">Compliance Notice</span>
                <span>Exported archive will contain signed cryptohashes verifying ledger authenticity for external auditor review.</span>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-[#F1F5F9]">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-3.5 py-2 border border-[#CBD5E1] text-[#475569] font-bold rounded-xl text-xs hover:bg-[#F8FAFC]"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  triggerToast('Audit log export generated and downloaded successfully.');
                  setShowExportModal(false);
                }}
                className="px-4 py-2 bg-[#0F4F4F] hover:bg-[#0A3D3D] text-white font-bold rounded-xl text-xs cursor-pointer flex items-center gap-1.5"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Download Archive</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
